import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { Cliente } from '../../../core/models/clientes/cliente';
import { IaConfiguracaoCliente, IaResposta } from '../../../core/models/ia/ia';

/**
 * Painel IA Napis (pedido-adicionar-itens-modal.component.html:31-192, boundaries verified via
 * `grep -n` against the CURRENT file — NOT html:94-255 as the original task brief said; the
 * file's real line numbers shifted after Tasks 1-6). Covers both asides toggled by
 * `iaProdutosPainelAberto`: the products-suggestion panel (`.ia-produtos-painel`) and the chat
 * panel (`.ia-painel-lateral`), plus the shared `.ia-overlay` backdrop — all mutually exclusive
 * views of the same feature, gated by the single `iaPainelAberto` flag.
 *
 * Deviations from the brief's Inputs/Outputs list (verified against the real template, not
 * assumed — see class-review notes below):
 * - `iaConfiguracao` @Input added: the real `iaModuloHabilitado()` body (ts:482-484) is
 *   `this.iaConfiguracao != null && this.iaConfiguracao.is_configurada == true &&
 *   this.iaConfiguracao.ia_ativa == true` — it reads `iaConfiguracao`, which the brief's Input
 *   list omitted entirely.
 * - `iaPergunta` @Input + `iaPerguntaChange` @Output added: html:187 (original numbering) has
 *   `[(ngModel)]="iaPergunta"` on the chat textarea, not covered by any Input/Output in the
 *   brief. `perguntarIa()` (ts:503-530, unmodified, stays in the parent) reads `this.iaPergunta`
 *   directly — a two-way binding is required so the parent's field holds the typed text when the
 *   parent handles the emitted `perguntaEnviada` event.
 * - `produtosIaClienteCompra()` / `produtosIaSegmentoNaoCompra()` copied here as local methods
 *   (ts:633-641 in the parent): pure filters of `this.iaSugestao` only (already an @Input here),
 *   same "local pure helper" pattern the brief itself prescribes for `condicoesProdutoIa`.
 * - `sugestaoAtualizada` @Output added: html:51 (original numbering) has
 *   `(click)="atualizarSugestaoProdutosIa()"`, not in the brief's Consumes list.
 *   `atualizarSugestaoProdutosIa()` (ts:586-598) calls `iaService.sugerirProdutosCliente(...)` and
 *   mutates `iaCarregando`/`iaProdutosPainelAberto`/`iaSugestao`/`iaProdutoAberto` — real
 *   business logic, so it stays in the parent and is only invoked via this new Output.
 *
 * `iaChatLista` @ViewChild reconnection (same bug class fixed twice already in Tasks 5/6): the
 * parent had `@ViewChild("iaChatLista") iaChatLista: ElementRef` (ts:294) targeting `#iaChatLista`
 * (html:165, original numbering), which lived inside this block and is now inside this component.
 * Exposed here as a public `ElementRef` so the parent reconnects via
 * `@ViewChild(PedidoItensIaPainelComponent)` + a private getter with the SAME name/type as the
 * original field, so the 2 guarded call sites in `rolarChatIa()` (ts:679-685) don't need to
 * change.
 */
@Component({
  selector: 'app-pedido-itens-ia-painel',
  standalone: false,
  templateUrl: './pedido-itens-ia-painel.component.html',
  styleUrls: ['./pedido-itens-ia-painel.component.scss']
})
export class PedidoItensIaPainelComponent {
  @Input() iaPainelAberto = false;
  @Input() iaProdutosPainelAberto = false;
  @Input() clienteSelecionado: Cliente = null;
  @Input() iaSugestao: IaResposta = null;
  @Input() iaProdutoAberto: number = null;
  @Input() iaConfiguracao: IaConfiguracaoCliente = null;
  @Input() iaConfiguracaoCarregando = false;
  @Input() iaHistoricoCarregando = false;
  @Input() iaMensagens: any[] = [];
  @Input() iaCarregando = false;
  @Input() iaPergunta = '';

  @Output() iaPerguntaChange = new EventEmitter<string>();
  @Output() painelFechado = new EventEmitter<void>();
  @Output() produtosPainelFechado = new EventEmitter<void>();
  @Output() sugestaoAtualizada = new EventEmitter<void>();
  @Output() perguntaEnviada = new EventEmitter<void>();
  @Output() perguntaEnviadaPorEnter = new EventEmitter<KeyboardEvent>();
  @Output() textoIaCopiado = new EventEmitter<string>();
  @Output() historicoRecarregado = new EventEmitter<boolean>();
  @Output() produtoIaAlternado = new EventEmitter<any>();

  /** Ver comentário da classe — reconexão do `@ViewChild("iaChatLista")` do pai. */
  @ViewChild('iaChatLista') iaChatLista: ElementRef;

  /** Corpo real copiado de `iaModuloHabilitado()` (ts:482-484) — NÃO o stub do brief. */
  moduloHabilitado(): boolean {
    return this.iaConfiguracao != null && this.iaConfiguracao.is_configurada == true && this.iaConfiguracao.ia_ativa == true;
  }

  /** Corpo real copiado de `condicoesProdutoIa()` (ts:648-651) — NÃO o stub do brief. */
  condicoesProdutoIa(produto: any): any[] {
    if (produto == null || produto.condicoes_especiais == null) return [];
    return produto.condicoes_especiais;
  }

  /** Copiado de `produtosIaClienteCompra()` (ts:633-636) — puro, depende só de `iaSugestao`. */
  produtosIaClienteCompra(): any[] {
    if (this.iaSugestao == null || this.iaSugestao.produtos_sugeridos == null) return [];
    return this.iaSugestao.produtos_sugeridos.filter(p => p.motivo == "historico_compra");
  }

  /** Copiado de `produtosIaSegmentoNaoCompra()` (ts:638-641) — puro, depende só de `iaSugestao`. */
  produtosIaSegmentoNaoCompra(): any[] {
    if (this.iaSugestao == null || this.iaSugestao.produtos_sugeridos == null) return [];
    return this.iaSugestao.produtos_sugeridos.filter(p => p.motivo != "historico_compra");
  }
}
